// Want List store — the viewer's own wants, for the in-binder STAR.
//
// Distinct from the per-binder selection basket (binder.svelte.js selectedCards)
// and from trade-mode selection: the star marks global, persistent Want List
// membership (ADR-0009), a different control with a different gesture and a
// different backing store. Never conflate them.
//
// The store holds a per-card optimistic override map keyed by a stable card
// identity string, so a tapped star flips instantly while the network write is
// in flight, and reverts on failure. Otherwise the filled state is the
// Viewer's want hits for the binder on screen (want-hits.svelte.js: the
// server's specificity-aware match, keyed to the Viewer's token). A tapped
// card keeps its override, so a write needs no refetch.

import { addWantEntry, removeWantEntriesMatching } from '../services/wantlist-api.js';
import { cardWantKey } from '../logic/wantlist-logic.js';
import { isWantHit } from './want-hits.svelte.js';

export const wantState = $state({
  /** @type {Record<string, boolean>} cardKey → optimistic wanted state */
  overrides: {},
});

// The override key is shared with the want-matching logic (wantlist-logic.js)
// so a tapped star and the server's match form agree on card identity (§9c).
export { cardWantKey };

/**
 * Is this card currently shown as wanted? An optimistic override (set by a tap)
 * wins; otherwise the Viewer's want hits for the binder decide.
 * @param {object} card
 * @param {string | null | undefined} binderUrl - the binder on screen
 */
export function isCardWanted(card, binderUrl) {
  const key = cardWantKey(card);
  if (key in wantState.overrides) return wantState.overrides[key];
  return isWantHit(binderUrl, card);
}

/**
 * Toggle the star for a card. Tap empty → add at full specificity; tap filled →
 * match-remove every entry that makes it wanted (ADR-0009's broad rule).
 * Optimistic; reverts the override on failure. Returns the new wanted state.
 *
 * @param {object} card - binder card data (name, set_code, collector_number, finish)
 * @param {boolean} currentlyWanted
 */
export async function toggleWant(card, currentlyWanted) {
  const key = cardWantKey(card);
  const next = !currentlyWanted;
  wantState.overrides = { ...wantState.overrides, [key]: next };

  const payload = {
    card_name: card.name || card.card_name,
    set_code: card.set_code || null,
    collector_number: card.collector_number || null,
    finish: card.finish || 'nonfoil',
  };

  try {
    const res = next
      ? await addWantEntry({ ...payload, source: 'star' })
      : await removeWantEntriesMatching(payload);
    if (!res.ok) throw new Error('want toggle failed');
    return next;
  } catch {
    // Revert the optimistic override on failure.
    const reverted = { ...wantState.overrides };
    reverted[key] = currentlyWanted;
    wantState.overrides = reverted;
    return currentlyWanted;
  }
}
